import { SignIn as ClerkSignIn } from "@clerk/react";
import { isBrowser } from "@repo/vihangam-yog-shared";
import { useEffect, useState } from "react";

import { CHAPTER } from "../chapter";
import { Mark } from "../components/Mark";

/**
 * Follows the OS/browser color-scheme preference, live — Clerk's
 * `appearance.variables` are literal color strings (no CSS var()
 * passthrough), so unlike the rest of this app — which reads
 * light/dark straight off our CSS custom properties — the Clerk-hosted
 * card needs its own copy of the two palettes picked in JS.
 */
function usePrefersDark(): boolean {
  const query = "(prefers-color-scheme: dark)";
  const [dark, setDark] = useState(
    () => isBrowser() && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setDark(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return dark;
}

/** Clerk's card renders in its own font unless told; this is the app's
 *  --font-sans stack (Clerk can't read CSS variables). */
const CLERK_TYPE = {
  fontFamily:
    '"Atkinson Hyperlegible Next", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  fontSize: "1rem",
};

/**
 * Admin sign-in. Hosts the Clerk-rendered SignIn component inside our
 * own frame so the page reads as the team app even though the form
 * itself is Clerk's.
 */
export function SignIn() {
  const dark = usePrefersDark();
  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-10 md:py-16">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3">
          <Mark size={42} decorative />
          <div className="flex min-w-0 flex-col">
            <span className="text-[15px] leading-snug font-semibold text-[color:var(--color-ink)]">
              {CHAPTER.name}
            </span>
            <span className="text-[14px] leading-snug text-[color:var(--color-ink-soft)]">
              Team app
            </span>
          </div>
        </div>

        <h1 className="page-title mt-8">Sign in</h1>

        {/* This Clerk instance is in RESTRICTED mode: sign-ups are
            disabled and the invitation link is the only way an account
            gets created. Telling people to "Sign up" pointed at a door
            that isn't there. */}
        <div className="mt-4 space-y-3 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          <p>
            <strong className="font-semibold text-[color:var(--color-ink)]">
              First time here?
            </strong>{" "}
            Open the invitation email we sent you and tap the link in it. That
            creates your account. Can&apos;t find it? Check your spam folder.
          </p>
          <p>
            <strong className="font-semibold text-[color:var(--color-ink)]">
              Been here before?
            </strong>{" "}
            Sign in below with the same email address.
          </p>
        </div>

        <div className="mt-8">
          <ClerkSignIn
            appearance={{
              elements: {
                rootBox: "w-full",
                card: "border border-[color:var(--color-line)] bg-[color:var(--color-paper)] shadow-md shadow-[color:var(--color-shadow)]/12",
              },
              // Only the base palette — buttons, links and focus rings
              // derive sensibly from colorPrimary on their own.
              // Clerk renamed its palette keys (colorText → colorForeground,
              // colorInputBackground → colorInput, …); the old names are
              // ignored at runtime, which left the card's text invisible in
              // dark mode. Current names first, legacy kept for older builds.
              variables: dark
                ? {
                    ...CLERK_TYPE,
                    colorBackground: "#251f18",
                    colorForeground: "#f2ece0",
                    colorMutedForeground: "#b3a996",
                    colorInput: "#17130f",
                    colorInputForeground: "#f2ece0",
                    colorBorder: "rgba(242, 236, 224, 0.18)",
                    colorMuted: "#1f1a14",
                    colorPrimary: "#e0834a",
                    colorPrimaryForeground: "#17130f",
                    colorNeutral: "#f2ece0",
                    colorInputBackground: "#17130f",
                    colorText: "#f2ece0",
                    colorTextSecondary: "#b3a996",
                    colorInputText: "#f2ece0",
                  }
                : CLERK_TYPE,
            }}
          />
        </div>
      </div>
    </div>
  );
}
